/**
 * assets/crest-api.js
 *
 * CompatibilityAPI — thin wrapper around the Crest AI USN validation
 * endpoint. Kept isolated from crest-ai.js so the network layer can be
 * swapped, mocked, or extended (retries, caching, auth headers) without
 * touching UI logic.
 *
 * Request:
 *   POST {endpoint}
 *   body: { "serial_number": "AAAA1234567" }
 *
 * Response (200, always JSON — the backend uses `status` to signal the
 * outcome rather than the HTTP status code):
 *   {
 *     "status": "INACTIVE" | "ACTIVE" | "EXPIRED" | "BLACKLISTED" |
 *               "INVALID_SERIAL_NUMBER" | "ERROR" | "User not found" | ...
 *     "valid_until": "2026-10-06 23:59:59" | "NA" | null,
 *     "message": "Human-readable message to show as-is in the popup.",
 *     "cta_enabled": "true" | "false"   // NOTE: string, not boolean
 *   }
 *
 * This class does not interpret `status` — that mapping (colors, icons,
 * whether the CTA unlocks) lives in crest-ai.js so copy/UX changes don't
 * require touching the network layer.
 *
 * Exposed on window.CrestAI.CompatibilityAPI (no other globals created).
 */
(function (window) {
  'use strict';

  var CrestAI = (window.CrestAI = window.CrestAI || {});

  function CompatibilityAPI(endpoint) {
    this.endpoint = endpoint || '';
  }

  /**
   * Validates a fully-formed USN against the backend. Callers are
   * responsible for validating the USN's shape client-side first — this
   * class never gets called while the user is still typing.
   *
   * @param {string} usn
   * @param {{ signal?: AbortSignal }} [options]
   * @returns {Promise<{ status: string, valid_until: (string|null), message: string, cta_enabled: (string|boolean) }>}
   */
  CompatibilityAPI.prototype.checkCompatibility = function (usn, options) {
    options = options || {};

    if (!this.endpoint) {
      return Promise.reject(new Error('CompatibilityAPI: no endpoint configured'));
    }

    return fetch(this.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ serial_number: usn }),
      signal: options.signal
    })
      .then(function (response) {
        // The backend encodes outcomes (invalid serial, blacklisted,
        // expired, etc.) as 200 responses with a `status` field, so a
        // non-2xx here means something genuinely went wrong in transit
        // (server down, gateway timeout, etc.) rather than a "valid"
        // negative result.
        if (!response.ok) {
          throw new Error('CompatibilityAPI: request failed with status ' + response.status);
        }
        return response.json();
      })
      .then(function (data) {
        if (!data || typeof data.status !== 'string' || typeof data.message !== 'string') {
          throw new Error('CompatibilityAPI: malformed response');
        }
        return data;
      });
  };

  CrestAI.CompatibilityAPI = CompatibilityAPI;
})(window);
